export type WeightedSkill = {
  name: string;
  weight: number;
};

export type WeightedRules = {
  weight: number;
  rules: string[];
};

export type TierConfig = {
  min: number;
  requirements: string[];
};

export type KeywordBuckets = Record<string, string[]>;

export type CustomSectionPosition = "top" | "before_scoring" | "before_tiers" | "before_keywords" | "bottom";

export type CustomSection = {
  title: string;
  content: string;
  position: CustomSectionPosition;
};

export type RoleConfig = {
  roleName: string;
  customSections?: CustomSection[];
  outputRequirements?: string[];
  scoring: {
    skills: WeightedSkill[];
    experience: WeightedRules;
    projects: WeightedRules;
    extra: WeightedRules;
  };
  tiers: {
    tier1: TierConfig;
    tier2: TierConfig;
    tier3: TierConfig;
  };
  keywords: {
    positive: KeywordBuckets;
    negative: KeywordBuckets;
  };
};

export const createEmptyRoleConfig = (roleName = "Frontend Engineer"): RoleConfig => ({
  roleName,
  customSections: [],
  outputRequirements: [
    "Return a concise reasoning summary.",
    "Return final score as a number.",
    "Return assigned tier (tier1, tier2, or tier3).",
    "Mention strongest positive and risk signals."
  ],
  scoring: {
    skills: [{ name: "React", weight: 20 }],
    experience: {
      weight: 25,
      rules: ["Give higher score for relevant domain experience."],
    },
    projects: {
      weight: 20,
      rules: ["Reward measurable project outcomes and ownership."],
    },
    extra: {
      weight: 10,
      rules: ["Consider communication, leadership, and mentoring signals."],
    },
  },
  tiers: {
    tier1: {
      min: 85,
      requirements: ["Strong core skills match", "Proven impact in similar role"],
    },
    tier2: {
      min: 70,
      requirements: ["Good skills match", "Some role-relevant project depth"],
    },
    tier3: {
      min: 55,
      requirements: ["Potential with partial match", "Needs targeted upskilling"],
    },
  },
  keywords: {
    positive: {
      frontend: ["react", "typescript", "performance", "accessibility"],
      teamwork: ["collaboration", "mentoring"],
    },
    negative: {
      risk: ["job hopping", "unclear ownership"],
    },
  },
});
